import { Link, NavLink, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useSlugScope, scoped } from "@/lib/scoped";
import {
  Trophy, ListChecks, Settings, ShieldCheck, LogOut, ChevronDown,
  FileText, Shield, Cookie, Users, Menu, Archive, Crown, Bell,
  Beer, PlayCircle, Building2, Ticket, Activity, Film,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Menu entries that should always resolve to a global (non-slugged) URL,
 * because they belong to the platform-level Super-Admin experience and
 * intentionally exit the current client environment.
 */
const GLOBAL_ONLY_TEST_IDS = new Set(["admin", "clients", "pitch", "demo", "promo-video"]);

/* ---------------------------------------------------------------------------
 * "More" menu — single source of truth for both desktop dropdown and mobile
 * sheet. Each entry declares its visibility rule so the component renders it
 * conditionally. `kind: "item"` = link, `kind: "section"` = a labelled header
 * followed by a separator (used to group Account / History / Legal).
 * ------------------------------------------------------------------------- */
const isAdmin = (u) => u?.role === "admin";
const isEditorOrAdmin = (u) => u?.role === "admin" || u?.is_editor;
// Master-only visibility: shown to editors/admins ONLY when they're on a
// tenant-neutral surface (no slug in the URL). Business/pitch tools are
// Dugsy-platform tools; they don't belong inside a client's own editor
// environment where the branding + data are all client-scoped.
const isEditorOrAdminOnMaster = (u, slug) => isEditorOrAdmin(u) && !slug;

const MORE_MENU = [
  { kind: "item", to: "/editor",         label: "Editor",          icon: ShieldCheck, testId: "editor",   show: isEditorOrAdmin },
  { kind: "item", to: "/key-monitors",   label: "Key monitors",    icon: Activity,    testId: "key-monitors", show: isEditorOrAdmin },
  { kind: "item", to: "/admin",          label: "Admin",           icon: Settings,    testId: "admin",    show: isAdmin },
  { kind: "item", to: "/editor/clients", label: "Client manager",  icon: Building2,   testId: "clients",  show: isAdmin },
  { kind: "item", to: "/business/pubs",  label: "Pitch to pubs",   icon: Beer,        testId: "pitch",    show: isEditorOrAdminOnMaster },
  { kind: "item", to: "/business/demo",  label: "Live demo",       icon: PlayCircle,  testId: "demo",     show: isEditorOrAdminOnMaster },
  { kind: "item", to: "/admin/promo-video", label: "Promo video",  icon: Film,        testId: "promo-video", show: isAdmin },
  { kind: "separator", show: isEditorOrAdmin },
  { kind: "section", label: "Account" },
  { kind: "item", to: "/settings",       label: "Settings",        icon: Bell,        testId: "settings" },
  { kind: "item", to: "/my-vouchers",    label: "My vouchers",     icon: Ticket,      testId: "my-vouchers" },
  { kind: "separator" },
  { kind: "section", label: "History" },
  { kind: "item", to: "/champions",       label: "Hall of Fame",         icon: Crown,   testId: "champions" },
  { kind: "item", to: "/seasons-archive", label: "Past seasons archive", icon: Archive, testId: "archive" },
  { kind: "separator" },
  { kind: "section", label: "Legal" },
  { kind: "item", to: "/terms",   label: "Terms of Service", icon: FileText, testId: "terms" },
  { kind: "item", to: "/privacy", label: "Privacy Policy",   icon: Shield,   testId: "privacy" },
  { kind: "item", to: "/cookies", label: "Cookie Policy",    icon: Cookie,   testId: "cookies" },
];

function renderMoreEntries(user, testIdPrefix, slug) {
  const out = [];
  MORE_MENU.forEach((entry, i) => {
    if (entry.show && !entry.show(user, slug)) return;
    if (entry.kind === "separator") {
      out.push(<DropdownMenuSeparator key={`sep-${i}`} />);
    } else if (entry.kind === "section") {
      out.push(
        <DropdownMenuLabel key={`lbl-${i}`} className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
          {entry.label}
        </DropdownMenuLabel>,
      );
    } else {
      const Icon = entry.icon;
      // Slug-aware routing: any link that has a slug-scoped counterpart
      // stays inside the current `/c/[slug]/*` environment. Super-Admin
      // only tools (Admin, Client Manager, Business Pubs, Business Demo)
      // are intentionally global and drop the client scope on click.
      const to = GLOBAL_ONLY_TEST_IDS.has(entry.testId) ? entry.to : scoped(entry.to, slug);
      out.push(
        <DropdownMenuItem key={entry.to} asChild data-testid={`${testIdPrefix}-${entry.testId}`}>
          <Link to={to} className="cursor-pointer">
            <Icon className="h-4 w-4 mr-2 text-zinc-500" />
            {entry.label}
          </Link>
        </DropdownMenuItem>,
      );
    }
  });
  return out;
}

/* ---------------------------------------------------------------------------
 * Subcomponents
 * ------------------------------------------------------------------------- */
const linkClass = ({ isActive }) =>
  `text-sm font-medium tracking-tight transition-colors duration-150 ${
    isActive
      ? "text-zinc-950 underline underline-offset-8 decoration-2"
      : "text-zinc-500 hover:text-zinc-950"
  }`;

function UserBadgeAndLogout({ user, onLogout }) {
  // Super-Admin gets a distinct outlined chip ("Signed in as <name> · Admin")
  // so it's always visually obvious which role is impersonating the app —
  // a subtle self-diagnostic that has already saved several "why am I on
  // this tenant?" support pings. Non-admin roles keep the pre-existing
  // muted uppercase name label.
  const isAdmin = user?.role === "admin";
  return (
    <div className="flex items-center gap-3">
      {isAdmin ? (
        <span
          className="hidden sm:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-zinc-700 border border-zinc-200 rounded-full px-2.5 py-1 bg-white"
          data-testid="admin-chip"
          title="Signed in as super-admin"
        >
          <ShieldCheck className="h-3 w-3 opacity-70" aria-hidden="true" />
          <span className="text-zinc-500 font-normal">Signed in as</span>
          <span className="text-zinc-950 font-semibold" data-testid="admin-chip-name">
            {user.name || user.email}
          </span>
          <span className="text-zinc-300" aria-hidden="true">·</span>
          <span className="text-zinc-950 font-bold" data-testid="admin-chip-role">
            Admin
          </span>
        </span>
      ) : (
        <span
          className="hidden sm:inline text-xs uppercase tracking-[0.15em] text-zinc-500"
          data-testid="user-name"
        >
          {user.name || user.email}
        </span>
      )}
      <button
        onClick={onLogout}
        data-testid="logout-btn"
        className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 border border-zinc-200 hover:border-zinc-950 hover:bg-zinc-950 hover:text-white transition-colors duration-200 active:scale-[0.98] rounded-sm"
      >
        <LogOut className="h-4 w-4" /> Logout
      </button>
    </div>
  );
}

function DesktopMoreMenu({ user, slug }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="nav-more"
          className="inline-flex items-center gap-1.5 text-sm font-medium tracking-tight text-zinc-500 hover:text-zinc-950 transition-colors duration-150 focus:outline-none"
        >
          <Menu className="h-4 w-4" /> More
          <ChevronDown className="h-3.5 w-3.5 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-white rounded-sm border-zinc-200 min-w-[220px]"
        data-testid="more-menu"
      >
        {renderMoreEntries(user, "more-menu", slug)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DesktopNav({ user, slug }) {
  return (
    <nav className="hidden md:flex items-center gap-8">
      <NavLink to={scoped("/", slug)} end className={linkClass} data-testid="nav-matches">
        <span className="inline-flex items-center gap-1.5"><ListChecks className="h-4 w-4" /> Predictions</span>
      </NavLink>
      <NavLink to={scoped("/leaderboard", slug)} className={linkClass} data-testid="nav-leaderboard">
        <span className="inline-flex items-center gap-1.5"><Trophy className="h-4 w-4" /> League</span>
      </NavLink>
      <NavLink to={scoped("/leagues", slug)} className={linkClass} data-testid="nav-leagues">
        <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" /> Mini-leagues</span>
      </NavLink>
      <DesktopMoreMenu user={user} slug={slug} />
    </nav>
  );
}

function MobileNav({ user, slug }) {
  return (
    <div className="md:hidden border-t border-zinc-100 bg-white">
      <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-5 overflow-x-auto" data-testid="mobile-primary-nav">
          <NavLink to={scoped("/", slug)} end className={linkClass} data-testid="nav-matches-mobile">Predictions</NavLink>
          <NavLink to={scoped("/leaderboard", slug)} className={linkClass} data-testid="nav-leaderboard-mobile">League</NavLink>
          <NavLink to={scoped("/leagues", slug)} className={linkClass} data-testid="nav-leagues-mobile">Mini-leagues</NavLink>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="nav-mobile-menu"
              aria-label="More menu"
              className="inline-flex items-center gap-1.5 text-sm font-medium tracking-tight text-zinc-700 hover:text-zinc-950 transition-colors duration-150 focus:outline-none px-2 py-1 -mr-1"
            >
              <Menu className="h-5 w-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white rounded-sm border-zinc-200 min-w-[220px]"
            data-testid="mobile-menu-dropdown"
          >
            {renderMoreEntries(user, "mobile-menu", slug)}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function ClientNav({ slug }) {
  // Strictly slug-scoped nav for role=client (brewery / pub-admin logins).
  // Bail out entirely if there's no slug in the URL — refuse to render
  // any link that could resolve to a global `/leaderboard` or `/settings`
  // (user directive: "no user should ever see a Global or Master
  // leaderboard"). This keeps the entire experience trapped inside
  // `/c/[slug]/*`.
  if (!slug) return null;
  const base = `/c/${slug}`;
  return (
    <nav
      className="hidden md:flex items-center gap-8"
      data-testid="client-nav"
    >
      <NavLink to={base} end className={linkClass} data-testid="client-nav-matches">
        <span className="inline-flex items-center gap-1.5">
          <ListChecks className="h-4 w-4" /> Predictions
        </span>
      </NavLink>
      <NavLink
        to={`${base}/leaderboard`}
        className={linkClass}
        data-testid="client-nav-leaderboard"
      >
        <span className="inline-flex items-center gap-1.5">
          <Trophy className="h-4 w-4" /> Local Standings
        </span>
      </NavLink>
      <NavLink
        to={`${base}/settings`}
        className={linkClass}
        data-testid="client-nav-settings"
      >
        <span className="inline-flex items-center gap-1.5">
          <Bell className="h-4 w-4" /> Settings
        </span>
      </NavLink>
    </nav>
  );
}

function ClientMobileNav({ slug }) {
  if (!slug) return null;
  const base = `/c/${slug}`;
  return (
    <div className="md:hidden border-t border-zinc-100 bg-white">
      <div
        className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-5 overflow-x-auto"
        data-testid="client-mobile-nav"
      >
        <NavLink to={base} end className={linkClass} data-testid="client-nav-matches-mobile">
          Predictions
        </NavLink>
        <NavLink
          to={`${base}/leaderboard`}
          className={linkClass}
          data-testid="client-nav-leaderboard-mobile"
        >
          Local Standings
        </NavLink>
        <NavLink
          to={`${base}/settings`}
          className={linkClass}
          data-testid="client-nav-settings-mobile"
        >
          Settings
        </NavLink>
      </div>
    </div>
  );
}

function ClientHeader({ user, slug, onLogout }) {
  return (
    <header
      className="sticky top-0 z-40 bg-white border-b border-zinc-200"
      data-testid="client-header"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Logo to={slug ? `/c/${slug}` : "/"} />
        <ClientNav slug={slug} />
        <UserBadgeAndLogout user={user} onLogout={onLogout} />
      </div>
      <ClientMobileNav slug={slug} />
    </header>
  );
}

/* ---------------------------------------------------------------------------
 * Public component
 * ------------------------------------------------------------------------- */
export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const slug = useSlugScope();

  if (!user) return null;

  const handleLogout = async () => {
    await logout();
    // When logging out inside a client env, land back on the branded
    // bare-slug login (`/[slug]`) so the user stays inside their pub's
    // experience. Outside any client env we go to the platform login.
    navigate(slug ? `/${slug}` : "/login");
  };

  if (user.role === "client") {
    return <ClientHeader user={user} slug={slug} onLogout={handleLogout} />;
  }

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-zinc-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Logo to={scoped("/", slug)} />
        <DesktopNav user={user} slug={slug} />
        <UserBadgeAndLogout user={user} onLogout={handleLogout} />
      </div>
      <MobileNav user={user} slug={slug} />
    </header>
  );
}
